/**
 * Auto-sync orchestration.
 *
 * Triggers a sync at three moments, each gated by an enabled flag in
 * localStorage:
 *
 *   1. On app launch — once, after a short post-boot delay
 *      (so the bridge / silent OAuth resume / SW registration all settle).
 *   2. On visibility change — when the tab regains focus.
 *   3. Periodically — every N minutes while the tab is open.
 *
 * All three are gated on:
 *   - autoSync.enabled === true (default)
 *   - syncManager has a configured + authenticated provider
 *   - global throttle so we don't sync more than once per MIN_SYNC_GAP_MS
 *
 * The sync pill on the launcher visualises live state already
 * (syncing… / ok / error) — we don't add new UI here.
 */

import { syncManager } from './syncManager.js';
import { syncState } from './syncState.js';
import { hasSharedBindings } from './sharedSync.js';

const PREFS_ENABLED = 'shell.autoSync.enabled';
const PREFS_INTERVAL_MIN = 'shell.autoSync.intervalMinutes';

/** Minimum time between two auto-sync attempts. */
const MIN_SYNC_GAP_MS = 30_000;
/** How long after page load before we kick off the launch sync. */
const LAUNCH_DELAY_MS = 4_000;
/** Default periodic interval if not configured. */
const DEFAULT_INTERVAL_MIN = 10;
/** Debounce visibility-change to avoid hammering when the user tabs quickly. */
const VISIBILITY_DEBOUNCE_MS = 1_500;

let lastSyncAtMs = 0;
let visibilityTimer: number | null = null;
let intervalTimer: number | null = null;
let initialised = false;

export function isAutoSyncEnabled(): boolean {
  const v = localStorage.getItem(PREFS_ENABLED);
  // Default: enabled
  return v === null ? true : v === 'true';
}

export function setAutoSyncEnabled(enabled: boolean): void {
  localStorage.setItem(PREFS_ENABLED, enabled ? 'true' : 'false');
  // Re-arm or tear down the periodic timer immediately.
  if (initialised) {
    armPeriodicTimer();
  }
}

export function getAutoSyncIntervalMinutes(): number {
  const raw = localStorage.getItem(PREFS_INTERVAL_MIN);
  const n = raw ? Number(raw) : DEFAULT_INTERVAL_MIN;
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_INTERVAL_MIN;
}

export function setAutoSyncIntervalMinutes(min: number): void {
  localStorage.setItem(PREFS_INTERVAL_MIN, String(Math.max(1, Math.floor(min))));
  if (initialised) armPeriodicTimer();
}

/**
 * Wire up auto-sync. Idempotent — safe to call multiple times.
 * Should be called once from the launcher bootstrap.
 */
export function initAutoSync(): void {
  if (initialised) return;
  initialised = true;

  // 1. Launch sync — small delay so providerRehydration finishes.
  window.setTimeout(() => maybeRunSync('launch'), LAUNCH_DELAY_MS);

  // 2. Visibility change.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (visibilityTimer != null) window.clearTimeout(visibilityTimer);
    visibilityTimer = window.setTimeout(() => {
      visibilityTimer = null;
      maybeRunSync('visibility');
    }, VISIBILITY_DEBOUNCE_MS);
  });

  // 3. Periodic.
  armPeriodicTimer();
}

/** F2: while any module is shared with other people, poll faster — Drive has
 *  no push, so the interval IS the freshness of everyone else's edits. */
const SHARED_INTERVAL_MS = 90_000;

function armPeriodicTimer(): void {
  if (intervalTimer != null) {
    window.clearInterval(intervalTimer);
    intervalTimer = null;
  }
  if (!isAutoSyncEnabled()) return;
  const configured = getAutoSyncIntervalMinutes() * 60_000;
  const ms = hasSharedBindings() ? Math.min(configured, SHARED_INTERVAL_MS) : configured;
  intervalTimer = window.setInterval(() => maybeRunSync('interval'), ms);
}

async function maybeRunSync(source: 'launch' | 'visibility' | 'interval' | 'manual'): Promise<void> {
  // Manual bypasses the enabled check — the user just pressed a button.
  if (source !== 'manual' && !isAutoSyncEnabled()) return;
  if (!syncManager.isReady()) return;
  // Don't pile up syncs if one is already running.
  if (syncState.snapshot.status === 'SYNCING') return;
  const now = Date.now();
  if (source !== 'manual' && now - lastSyncAtMs < MIN_SYNC_GAP_MS) return;
  lastSyncAtMs = now;
  try {
    await syncManager.syncNow();
  } catch (e) {
    console.warn(`[autoSync] sync (${source}) failed`, e);
  }
}

/** Trigger a sync now from the UI (button click on the launcher pill). */
export async function manualSync(): Promise<void> {
  return maybeRunSync('manual');
}

/** Re-arm the periodic timer — call after shared bindings change so the
 *  faster shared cadence starts/stops without a reload. */
export function refreshAutoSyncCadence(): void {
  if (initialised) armPeriodicTimer();
}
